import { CreateFYIGroupsRequest, CreateFYIGroupsResponse } from '../../../../../../../queries/use-create-fyi-groups';
import { OnboardingFormEntry } from './FYIGroupsOnboarding';
import plural from '../../../../../../../utilities/plural';

type InvalidEntitiesWithinGroupType = {
    [groupId: number]: {
        groupWithoutDayAndMonthValue: boolean;
        groupWithoutDayValue: boolean;
        entitiesWithoutDayAndMonthValues: number[];
        entitiesWithoutDayValues: number[];
    };
};

type Accumulator = { dayMonthError?: number; dayError?: number; invalidExpandedGroups?: OnboardingFormEntry }[];
// Format input field error (for rows without day/month values) before sending request to create FYI groups
export function formatGroupRowsError(allData: OnboardingFormEntry[]): {
    groupsWithoutDayAndMonthValues: number[];
    groupsWithoutDayValues: number[];
    invalidEntitiesWithinGroup: InvalidEntitiesWithinGroupType;
} {
    // const selectedGroups = allData.filter((d) => d.group.isSelected);
    const errorGroups = allData.reduce(function (acc: Accumulator, { group, entities }: OnboardingFormEntry, i: number) {
        if (group.isSelected && !group.isExpanded && !group.month && !group.day) {
            acc.push({ dayMonthError: i });
        } else if (group.isSelected && !group.isExpanded && group.month && !group.day) {
            acc.push({ dayError: i });
        } else if (
            group.isSelected &&
            group.isExpanded &&
            group.hasExpandedEntities &&
            (!group.month || !group.day) &&
            entities.some((e) => e.isSelected && (!e.day || !e.month))
        ) {
            acc.push({ invalidExpandedGroups: { group, entities } });
        }
        return acc;
    }, []);

    const groupsWithoutDayAndMonthValues = errorGroups.map((err) => err.dayMonthError).filter((error) => error !== undefined) as number[];
    const groupsWithoutDayValues = errorGroups.map((err) => err.dayError).filter((error) => error !== undefined) as number[];
    const invalidExpandedGroups = errorGroups.map((err) => err.invalidExpandedGroups).filter((error) => error) as OnboardingFormEntry[];

    const invalidEntitiesWithinGroup: InvalidEntitiesWithinGroupType = {};

    for (const invalidGroup of invalidExpandedGroups) {
        const invalidGroupIndex = allData.findIndex((d) => d.group.fyiGroup.id === invalidGroup.group.fyiGroup.id);
        const invalidEntities = invalidGroup.entities.filter((ent) => !ent.day || !ent.month);

        const entitiesWithoutDayAndMonthValues: number[] = [];
        const entitiesWithoutDayValues: number[] = [];

        for (const invalidEntity of invalidEntities) {
            const invalidEntityIndex = allData[invalidGroupIndex].entities.findIndex((e) => e.fyiEntity.id === invalidEntity.fyiEntity.id);
            if (!invalidEntity.month) {
                entitiesWithoutDayAndMonthValues.push(invalidEntityIndex);
            }
            if (!invalidEntity.day) {
                entitiesWithoutDayValues.push(invalidEntityIndex);
            }
        }

        invalidEntitiesWithinGroup[invalidGroupIndex] = {
            groupWithoutDayAndMonthValue: !invalidGroup.group.day && !invalidGroup.group.month,
            groupWithoutDayValue: Boolean(invalidGroup.group.month) && !invalidGroup.group.day,
            entitiesWithoutDayAndMonthValues: entitiesWithoutDayAndMonthValues,
            entitiesWithoutDayValues: entitiesWithoutDayValues,
        };
    }

    return { groupsWithoutDayAndMonthValues, groupsWithoutDayValues, invalidEntitiesWithinGroup };
}

// Format data chunk into CreateFYIGroupsRequest format
export function formatGroupsChunk(data: OnboardingFormEntry[]): CreateFYIGroupsRequest {
    return data.map((c) => {
        return {
            id: c.group.fyiGroup.id,
            name: c.group.fyiGroup.name,
            financialYearEndDay: c.group.day,
            financialYearEndMonth: c.group.month,
            createAllEntities: c.group.hasExpandedEntities === false,
            entities: c.group.hasExpandedEntities
                ? c.entities
                      .filter((e) => Boolean(e.day || c.group.day) && Boolean(e.month || c.group.month) && e.isSelected)
                      .map((e) => {
                          return {
                              id: e.fyiEntity.id,
                              name: e.fyiEntity.name,
                              businessStructure: e.fyiEntity.details.businessStructure || 'OTHER',
                              financialYearEndDay: (e.day || c.group.day) as number,
                              financialYearEndMonth: (e.month || c.group.month) as number,
                          };
                      })
                : undefined,
        };
    });
}

// Format error messages object into a string array
export function formatErrorMessages(messages?: { [groupName: string]: string[] }): string[] {
    if (!messages) {
        return [];
    }

    const errorMessages: string[] = [];
    for (const key in messages) {
        for (const i in messages[key]) {
            errorMessages.push(`${key} : ${messages[key][i]}`);
        }
    }
    return errorMessages;
}

// Format success messages object into string array
export function formatSuccessMessages(data?: CreateFYIGroupsResponse): string[] {
    if (!data) {
        return [];
    }

    const successMessages: string[] = [];
    const { groupsOnboarded, existingGroupsMapped, entitiesOnboarded, existingEntitiesMapped } = data;

    if (groupsOnboarded) {
        successMessages.push(`${groupsOnboarded} new ${plural(groupsOnboarded, 'group', 'groups')} onboarded.`);
    }

    if (existingGroupsMapped) {
        successMessages.push(`${existingGroupsMapped} existing ${plural(existingGroupsMapped, 'group', 'groups')} mapped to FYI.`);
    }

    if (entitiesOnboarded) {
        successMessages.push(`${entitiesOnboarded} new ${plural(entitiesOnboarded, 'entity', 'entities')} onboarded.`);
    }

    if (existingEntitiesMapped) {
        successMessages.push(`${existingEntitiesMapped} existing ${plural(existingEntitiesMapped, 'entity', 'entities')} mapped to FYI.`);
    }

    return successMessages;
}
